const _ = require("lodash");
const ErrorUtil = require("../../../../shared/ErrorUtil");
const ImmCheck = require("../../util/ImmCheck");
const {FLAGS} = require("../../util/GlobalFlags");
const diffObjects = require("../../util/diffObjects");
const AT = require("../ActionTypes");
const {getLoadedBundles} = require("../../Bundles");

const CHECK_FOR_VAULT_MUTATIONS = false;

const checkForMutations = function(state, action) {
	console.log("Vault immutability verification is ON."); // eslint-disable-line no-console
	const oldStateClone = _.cloneDeep(state);
	setTimeout(() => {
		if (!_.isEqual(state, oldStateClone)) {
			ErrorUtil.log(
				"E_VAULT_MUTATION",
				"An action mutated the vault directly",
				{
					action,
					diff: diffObjects(oldStateClone, state),
				}
			);
		}
	}, 0);
};

let wrapWithImmCheck;
if (process.env.NODE_ENV === "production") {
	wrapWithImmCheck = x => x;
} else {
	wrapWithImmCheck = function(x) {
		if (FLAGS.useProxyImmCheck) {
			const onError = (target, name, oldValue, newValue) =>
				ErrorUtil.log("E_VAULT_MUTATION", "Vault state was mutated directly", {
					target,
					name,
					oldValue,
					newValue,
				});
			return ImmCheck.wrapWithImmCheck(x, onError);
		}
		return x;
	};
}

// Quick implementation of an array where inserted elements have priorities
// and the order of the array is always in ascending priority order, with ties
// broken by earliest inserted element first. Used for update hooks.
const PriorityArray = function() {
	this.els = [];
	this.priorities = [];
};

PriorityArray.prototype.get = function() {
	return this.els;
};

PriorityArray.prototype.add = function(el, priority = 0) {
	const curr = this.priorities;
	let i = 0;
	// Since all uses of priority array are small, just do a linear search.
	while (i < curr.length && curr[i] <= priority) {
		i += 1;
	}
	this.els.splice(i, 0, el);
	this.priorities.splice(i, 0, priority);
};

// Exported class

/**
 * Was getting "Declaration emit for this file requires using private name".
 * This `type` annotation is a workaround. See: https://stackoverflow.com/questions/60009092/declaration-will-not-emit-due-to-private-name-usage
 * @type {unknown}
 */
const Updater = function() {
	if (!(this instanceof Updater)) {
		return new Updater();
	}
	this.actionHandlers = {};
	this.preUpdateHooks = new PriorityArray();
	this.postUpdateHooks = new PriorityArray();
	this.lastHooksToModify = [];
};

// Mutates the action to have no sensitive data, like passwords, and returns
// it. Extremely important that only cleaned actions are logged.
Updater.prototype.cleanAction = function(action) {
	delete action.originalPassword;
	delete action.password;
	delete action.passwordRepeat;
	delete action.token;
	if (
		_.includes(["account", "shipping", "billing", "creditCard"], action.section)
	) {
		delete action.val;
		delete action.vals;
	}

	if (_.get(action, "data.user.password")) {
		action.data.user.password = "<cleaned>";
	}

	// Things below are not sensitive data but can be enormous.
	if (action.payload) {
		action.payload = "<cleaned>";
	}
	if (action.type === AT.LOAD_LIBRARY_PAGES_SUCCESS && action.data) {
		action.data = "<cleaned>";
	}
	if (action.type === AT.PLAY_BLOCK_SUCCESS && _.get(action, "data.problems")) {
		action.data = _.clone(action.data);
		action.data.problems = "<cleaned>";
	}
	if (
		action.type === AT.PLAY_BLOCK_SUCCESS &&
		_.get(action, "data.instructionsProblems")
	) {
		action.data = _.clone(action.data);
		action.data.instructionsProblems = "<cleaned>";
	}
	return action;
};

// Compute the new state the vault will have after applying the action. Also
// triggers the side effects of vault plugins.
Updater.prototype.update = function(vault, action) {
	const {actionHandlers, preUpdateHooks, postUpdateHooks} = this;
	if (!action.type || !actionHandlers[action.type]) {
		let message = "Unrecognized action";
		if (action.type) {
			message += ` (${action.type})`;
		}
		const bundles = getLoadedBundles();
		const sentryAction = action;
		if (bundles.length) {
			sentryAction.loadedBundles = bundles;
		}
		ErrorUtil.log("E_UNRECOGNIZED_ACTION", message, sentryAction);
		throw new Error(message);
	}

	const oldState = wrapWithImmCheck(vault.state);
	let newState = oldState;
	this.lastHooksToModify = [];

	if (CHECK_FOR_VAULT_MUTATIONS) {
		checkForMutations(newState, action);
	}

	vault.clock.freezeNow();

	preUpdateHooks.get().forEach(({f, name}) => {
		const postHookState = f(vault, newState, action) || newState;
		if (postHookState !== newState) {
			this.lastHooksToModify.push(name);
		}
		newState = postHookState;
	});

	newState = actionHandlers[action.type](vault, newState, action) || newState;

	postUpdateHooks.get().forEach(({f, name}) => {
		const postHookState = f(vault, oldState, newState, action) || newState;
		if (postHookState !== newState) {
			this.lastHooksToModify.push(name);
		}
		newState = postHookState;
	});

	vault.clock.unfreezeNow();

	this.cleanAction(action);

	return wrapWithImmCheck(newState);
};

// The passed function should take (vault, state, action). You can also pass an
// object mapping types to functions to add many at once.
Updater.prototype.addActionHandler = function(type, f) {
	if (typeof type === "string") {
		this.actionHandlers[type] = f;
	} else if (type) {
		_.each(type, (func, typeName) => (this.actionHandlers[typeName] = func));
	}
};

// The passed function should take (vault, state, action).
Updater.prototype.addPreUpdateHook = function(f, name, priority) {
	this.preUpdateHooks.add({f, name}, priority);
};

// The passed function should take (vault, oldState, newState, action).
Updater.prototype.addPostUpdateHook = function(f, name, priority) {
	this.postUpdateHooks.add({f, name}, priority);
};

// Get the names of all the hook functions that actually modified the state.
// TODO: make it so it doesn't miss hooks that only emitted events.
Updater.prototype.getLastModifyingHooks = function() {
	return this.lastHooksToModify;
};

module.exports = Updater;
